import React, { useState, useEffect, useRef } from 'react';
import { buildApiUrl } from '../utils/api';

interface Company {
  companyId: string;
  companyName: string;
  companyUrl: string;
  companyState: string;
  isPrimary?: boolean;
  primaryContact?: string;
  primaryPhone?: string;
  primaryEmail?: string;
}

interface CompanySearchDropdownProps {
  value: string;
  onChange: (companyId: string, company: Company | null) => void;
  onNewCompanyRequest: () => void;
  placeholder?: string;
  className?: string;
  required?: boolean;
}

export default function CompanySearchDropdown({
  value,
  onChange,
  onNewCompanyRequest,
  placeholder = 'Search for a company...',
  className = '',
  required = false,
}: CompanySearchDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Load companies when dropdown opens
  useEffect(() => {
    if (isOpen && searchTerm.trim() === '') {
      loadRecentCompanies();
    }
  }, [isOpen]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load recent companies (no search term)
  const loadRecentCompanies = async () => {
    try {
      setIsLoading(true);
      const res = await fetch(buildApiUrl('/companies'));
      if (res.ok) {
        const data = await res.json();
        // Extract companies array from the response structure
        const companiesArray = data.companies || data || [];
        setCompanies(companiesArray.slice(0, 10)); // Limit to 10 recent companies
      }
    } catch (err) {
      console.error('Failed to load companies:', err);
      setCompanies([]); // Ensure companies is always an array
    } finally {
      setIsLoading(false);
    }
  };

  // Search companies with debouncing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm.trim()) {
        searchCompanies(searchTerm);
      } else if (isOpen) {
        loadRecentCompanies();
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const searchCompanies = async (term: string) => {
    try {
      setIsLoading(true);
      const res = await fetch(
        buildApiUrl(`/search-companies?search=${encodeURIComponent(term)}&limit=20`)
      );
      if (res.ok) {
        const data = await res.json();
        // Extract companies array from the response structure
        const companiesArray = data.companies || data || [];
        setCompanies(companiesArray);
      }
    } catch (err) {
      console.error('Failed to search companies:', err);
      setCompanies([]); // Ensure companies is always an array
    } finally {
      setIsLoading(false);
    }
  };

  const handleCompanySelect = (company: Company) => {
    setSelectedCompany(company);
    onChange(company.companyId, company);
    setSearchTerm(company.companyName);
    setIsOpen(false);
  };

  const handleNewCompanyClick = () => {
    setIsOpen(false);
    onNewCompanyRequest();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (!isOpen) setIsOpen(true);

    // Clear selection if user is typing
    if (selectedCompany && value !== selectedCompany.companyName) {
      setSelectedCompany(null);
      onChange('', null);
    }
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const formatCompanyDisplay = (company: Company) => {
    return `${company.companyName} (${company.companyState}) - ${company.companyUrl}`;
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <input
        type='text'
        value={searchTerm}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        placeholder={placeholder}
        className='w-full p-2 border border-white/10 bg-white/5 text-slate-100 placeholder:text-slate-400 rounded focus:outline-none focus:ring-2 focus:ring-indigo-400'
        required={required}
      />

      {isOpen && (
        <div className='absolute z-50 w-full mt-1 bg-slate-800 border border-white/10 rounded-md shadow-lg max-h-60 overflow-auto'>
          {/* Create New Company Option - Always at top */}
          <button
            type='button'
            onClick={handleNewCompanyClick}
            className='w-full text-left px-3 py-2 text-blue-400 hover:bg-white/10 border-b border-white/10 font-medium transition-colors'
          >
            + Create New Company
          </button>

          {/* Company Results */}
          {isLoading ? (
            <div className='px-3 py-2 text-slate-400 text-sm'>Searching...</div>
          ) : (companies || []).length > 0 ? (
            (companies || []).map(company => (
              <button
                key={company.companyId}
                type='button'
                onClick={() => handleCompanySelect(company)}
                className='w-full text-left px-3 py-2 hover:bg-white/10 border-b border-white/10 last:border-b-0 transition-colors'
              >
                <div className='flex items-center gap-2'>
                  <span className='font-medium text-slate-100'>{company.companyName}</span>
                  {company.isPrimary && (
                    <span className='text-xs bg-blue-600 text-white px-2 py-1 rounded-full'>
                      Primary
                    </span>
                  )}
                </div>
                <div className='text-sm text-slate-300'>
                  {company.companyState} • {company.companyUrl}
                </div>
                {company.primaryContact && (
                  <div className='text-xs text-slate-400'>Contact: {company.primaryContact}</div>
                )}
              </button>
            ))
          ) : searchTerm.trim() ? (
            <div className='px-3 py-2 text-slate-400 text-sm'>
              No companies found matching "{searchTerm}"
            </div>
          ) : (
            <div className='px-3 py-2 text-slate-400 text-sm'>Start typing to search companies</div>
          )}
        </div>
      )}
    </div>
  );
}
